import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RECURRENCE_CYCLES,
  RECURRENCE_CYCLE_MAP,
  RECURRING_STATUSES,
  RECURRING_STATUS_MAP,
  type RecurrenceCycle,
  type RecurringStatus,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import {
  exportDay,
  exportNotes,
  exportPeriod,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface RecurringBillRow {
  id: string;
  title: string;
  status: RecurringStatus;
  cycle: RecurrenceCycle;
  startDate: string;
  endDate: string | null;
  maxRuns: number | null;
  nextRunAt: string;
  lastRunAt: string | null;
  issuedCount: number;
  periodTotal: number;
  autoReceive: boolean;
  supplier: { id: string; name: string };
}

/**
 * أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في recurring-bills.service.ts.
 *
 * قيمة الدورة وعدد ما صدر محسوبان بعد القراءة لا حقلان في القاعدة، فلا
 * يُفلتر عليهما في الخادم.
 */
const RECURRING_BILL_FILTERS: FilterDef[] = [
  { key: 'title', label: 'الاشتراك', kind: 'text', sortable: true },
  { key: 'supplier', label: 'المورد', kind: 'text' },
  {
    key: 'cycle',
    label: 'الدورة',
    kind: 'enum',
    options: RECURRENCE_CYCLES.map((c) => ({ value: c, label: RECURRENCE_CYCLE_MAP[c].label })),
  },
  { key: 'nextRunAt', label: 'الإصدار التالي', kind: 'date', sortable: true },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: RECURRING_STATUSES.map((s) => ({ value: s, label: RECURRING_STATUS_MAP[s].label })),
  },
];

interface RecurringBillPage {
  items: RecurringBillRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * الاشتراكات الدورية للموردين.
 *
 * الإيجار والإنترنت واشتراكات البرامج: فواتير موردين تتكرر كل شهر بالمبلغ
 * نفسه. تُسجَّل هنا مرة فتظهر في فواتير الموردين وحدها في موعدها، ولا تُنسى
 * فتبدو الذمم أقل من حقيقتها.
 */
export function RecurringBillsList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);

  const canManage = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['recurring-bills', query],
    queryFn: () => api<RecurringBillPage>(`/finance/recurring-bills${qs(query)}`),
  });

  const runDue = useMutation({
    mutationFn: () =>
      api<{ issued: number; failed: number }>('/finance/recurring-bills/run-due', {
        method: 'POST',
      }),
    onSuccess: () => void queryClient.invalidateQueries(),
  });

  const rows = list.data?.items ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const due = rows.filter((r) => r.status === 'ACTIVE' && r.nextRunAt <= today);
  const monthly = rows
    .filter((r) => r.status === 'ACTIVE')
    .reduce((s, r) => s + r.periodTotal / RECURRENCE_CYCLE_MAP[r.cycle].months, 0);

  /* التصدير: كل الاشتراكات المطابقة للبحث والحالة والمدى والفلاتر، لا الصفحة وحدها. */
  const buildExport = async (): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<RecurringBillRow>(
      '/finance/recurring-bills',
      query,
      100,
    );
    const status = state.status as RecurringStatus | undefined;
    return {
      title: 'الاشتراكات الدورية للموردين',
      sheets: [
        {
          title: 'اشتراكات الموردين',
          period: exportPeriod(state.from, state.to),
          sections: [
            {
              columns: [
                { key: 'title', label: 'الاشتراك' },
                { key: 'supplier', label: 'المورد' },
                { key: 'cycle', label: 'الدورة' },
                { key: 'periodTotal', label: 'قيمة الدورة', kind: 'money', total: true },
                { key: 'nextRunAt', label: 'الإصدار التالي', kind: 'date' },
                { key: 'issuedCount', label: 'صدرت', kind: 'number' },
                { key: 'mode', label: 'وضع الإصدار' },
                { key: 'status', label: 'الحالة' },
              ],
              rows: items.map((r) => ({
                title: r.title,
                supplier: r.supplier.name,
                cycle: RECURRENCE_CYCLE_MAP[r.cycle].label,
                periodTotal: r.periodTotal,
                nextRunAt: r.status === 'ENDED' ? null : exportDay(r.nextRunAt),
                issuedCount: r.issuedCount,
                mode: r.autoReceive ? 'استلام وترحيل' : 'مسودة للمراجعة',
                status: RECURRING_STATUS_MAP[r.status]?.label ?? r.status,
              })),
            },
          ],
          notes: exportNotes({
            q: debounced,
            conditions: [
              status && `الحالة: ${RECURRING_STATUS_MAP[status]?.label ?? status}`,
              ...describeFilters(RECURRING_BILL_FILTERS, filters),
            ],
            count: items.length,
            total,
            truncated,
          }),
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="الاشتراكات الدورية للموردين"
        subtitle={list.data ? `${list.data.total} اشتراكًا` : undefined}
        actions={
          canManage && (
            <>
              {due.length > 0 && (
                <button
                  className="btn"
                  onClick={() => runDue.mutate()}
                  disabled={runDue.isPending}
                >
                  {runDue.isPending ? 'جارٍ الإصدار' : `إصدار المستحق (${due.length})`}
                </button>
              )}
              <Link
                to={`/finance/recurring-bills/new${state.supplierId ? `?supplierId=${state.supplierId}` : ''}`}
                className="btn btn-primary"
              >
                اشتراك مورد جديد
              </Link>
            </>
          )
        }
      />

      <div className="stats">
        <Stat label="فعّالة" value={rows.filter((r) => r.status === 'ACTIVE').length} />
        <Stat label="مستحقة الآن" value={due.length} />
        {/* الربع سنوي يُقسَم على ثلاثة حتى تُقارن الأرقام على أساس شهري واحد. */}
        <Stat
          label="التزام متكرر شهريًا"
          value={<span className="num">{money(monthly)}</span>}
        />
      </div>

      {runDue.isSuccess && (
        <div className="alert alert-info mb-4">
          {runDue.data.issued > 0
            ? `سُجّلت ${runDue.data.issued} فاتورة مورد. تجدها في فواتير الموردين مسودات ما لم يكن الاشتراك مضبوطًا على الاستلام التلقائي.`
            : 'لا فواتير مستحقة الآن.'}
          {runDue.data.failed > 0 && ` وتعذّر تسجيل ${runDue.data.failed}.`}
        </div>
      )}

      {runDue.isError && (
        <div className="alert alert-danger mb-4">
          {runDue.error instanceof Error ? runDue.error.message : 'تعذّر الإصدار.'}
        </div>
      )}

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالعنوان أو المورد"
        />

        <select
          className="select"
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
        >
          <option value="">كل الحالات</option>
          {RECURRING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {RECURRING_STATUS_MAP[s].label}
            </option>
          ))}
        </select>

        {/* المدى على «الإصدار التالي»: ما سيُسجَّل من فواتير بين اليومين. */}
        <DateRangeFilter
          from={state.from}
          to={state.to}
          onChange={(f, t) => patch({ from: f, to: t })}
        />

        <ExportMenu build={buildExport} disabled={!list.data?.total} />

        {state.supplierId && (
          <button className="btn btn-sm" onClick={() => patch({ supplierId: undefined })}>
            كل الموردين
          </button>
        )}

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={RECURRING_BILL_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={RECURRING_BILL_FILTERS} filters={filters} onChange={setFilters} />

      {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

      <FilterScope
        defs={RECURRING_BILL_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/finance/recurring-bills/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الاشتراك
                  <HeaderFilter colKey="title" />
                </th>
                <th>
                  المورد
                  <HeaderFilter colKey="supplier" />
                </th>
                <th>
                  الدورة
                  <HeaderFilter colKey="cycle" />
                </th>
                <th>قيمة الدورة</th>
                <th>
                  الإصدار التالي
                  <HeaderFilter colKey="nextRunAt" />
                </th>
                <th>صدرت</th>
                <th>
                  الحالة
                  <HeaderFilter colKey="status" />
                </th>
              </tr>
            </thead>

            {list.isLoading && <TableSkeleton rows={5} cols={7} />}

            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/finance/recurring-bills/${r.id}`} className="cell-strong link">
                      {r.title}
                    </Link>
                    {r.autoReceive && <div className="tiny mute">استلام وترحيل تلقائي</div>}
                  </td>
                  <td className="cell-mute">{r.supplier.name}</td>
                  <td className="cell-mute">{RECURRENCE_CYCLE_MAP[r.cycle].label}</td>
                  <td className="num">{money(r.periodTotal)}</td>
                  <td
                    className={`num ${
                      r.status === 'ACTIVE' && r.nextRunAt <= today ? 'neg' : 'cell-mute'
                    }`}
                  >
                    {r.status === 'ENDED' ? '—' : date(r.nextRunAt)}
                  </td>
                  <td className="num cell-mute">
                    {r.issuedCount}
                    {r.maxRuns ? ` / ${r.maxRuns}` : ''}
                  </td>
                  <td>
                    <Badge kind={RECURRING_STATUS_MAP[r.status].kind}>
                      {RECURRING_STATUS_MAP[r.status].label}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!list.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا اشتراكات دورية للموردين"
            text="سجّل الإيجار أو الإنترنت أو اشتراك برنامج مرة واحدة، فتُنشأ فاتورة المورد وحدها في موعدها كل دورة."
          />
        )}

        {list.data && (
          <Pager
            page={page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      <p className="tiny mute mt-3">
        يفحص النظام الاشتراكات كل ساعة ويُسجّل ما استحق. الفواتير تُنشأ مسودات ليراجع أحد المبلغ
        ويستلمها فتُرحَّل، ما لم يُفعَّل «الاستلام التلقائي» في الاشتراك نفسه.
      </p>
    </div>
  );
}
